import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useSlide } from '../../lib/useSlide';
import { useSheetFocus } from '../../lib/useSheetFocus';

// THE DRAWER — the app's one bottom sheet (Freddi, 2026-10-09: "a drawer that
// slides out from the bottom … standard and global"). A scrim, then a panel
// that rises from the foot of the screen to just short of its top and sides —
// the page shows round it — and sinks back when it is dismissed. The customer
// app's request wizard is its first tenant; anything that is somewhere to DO
// more, rather than one question, belongs in it. One question is a dialog
// (LeaveDialog).
//
// THE LIFECYCLE IS `useSlide`, the drawer lifecycle the desk's sheet and rail
// already follow: the open flag and its phase are one piece of state, so the
// panel wears `in` in the very first frame it paints; the phase ends with the
// animation, never with a stopwatch; reduced motion skips the phase. While
// the panel is on its way out it stays mounted — that is what the slide down
// is — and when it has gone the content is unmounted, so it opens fresh.
//
// THE PANEL IS THE SCROLL. The content's own pinned foot (`position: sticky`)
// pins to the panel's bottom, which is the screen's. At rest the panel
// carries no transform, so a `fixed` dialog inside it still takes the viewport
// as its frame.
//
// IT CAN BE PULLED DOWN (Freddi, 2026-10-09: "drag from the top of the drawer
// down to close"). The grip at its top follows the finger; the scrim thins as
// the sheet goes; let go past a quarter of the height, or with a flick, and
// the sheet asks to be dismissed — through `onClose`, the same gate as any
// other way out, so content with something to lose can still ask first. If
// the content closes, the slide down starts from where the finger left it;
// if it does not — a question went up instead — the sheet springs back.
//
// It takes focus when it opens and keeps Tab inside; Escape is `onClose` too.

export interface DrawerProps {
  open: boolean;
  /** What the drawer holds, for the dialog's name. */
  label: string;
  /** Asked to close — by Escape, or by a pull on the grip. The content decides. */
  onClose?: () => void;
  /** A shorter sheet of one fixed height, for a drawer with little in it (Freddi, 2026-10-13). */
  compact?: boolean;
  children: ReactNode;
}

export function Drawer({ open, label, onClose, compact = false, children }: DrawerProps) {
  const slide = useSlide(open);
  useEffect(() => { slide.setOpen(open); }, [open, slide]);
  const shown = slide.open || slide.phase === 'out';
  // The page behind does not scroll while the drawer is up: a thumb that
  // overshoots the panel should not drag Home along with it.
  useEffect(() => {
    if (!shown || typeof document === 'undefined') return;
    const root = document.documentElement;
    const was = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => { root.style.overflow = was; };
  }, [shown]);
  if (!shown) return null;
  return <Panel label={label} open={slide.open} phase={slide.phase} onAnimationEnd={slide.onAnimationEnd} onClose={onClose} compact={compact}>{children}</Panel>;
}

/** How long a pull past the threshold holds where the finger left it, for the content's close to land. */
const CLOSE_GRACE_MS = 500;
/** Past this share of the sheet's height, or this speed, a pull is a dismissal. */
const PULL_SHARE = 0.25;
const FLICK_PX_PER_MS = 0.6;

function Panel({ label, open, phase, onAnimationEnd, onClose, compact, children }: { label: string; compact: boolean; open: boolean; phase: '' | 'in' | 'out'; onAnimationEnd: (e: React.AnimationEvent<Element>) => void; onClose?: () => void; children: ReactNode }) {
  // ONE CLOSE FOR THE SHEET'S LIFETIME: the parent may hand a new inline
  // callback on every render (a quiet read answering behind the drawer), and
  // the focus hook would tear down and refocus the sheet on each — stealing
  // focus from a field mid-word (#245 review). The latest callback lives in a
  // ref; the hook sees one function.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const askClose = useCallback(() => closeRef.current?.(), []);
  const box = useSheetFocus(onClose ? askClose : undefined);
  // The pull: `drag` is the live offset while the finger is down; `settle`
  // is a release that did not close, springing back on a transition.
  const [drag, setDrag] = useState<number | null>(null);
  const [settle, setSettle] = useState(false);
  // A release past the threshold, waiting to learn whether the content closed.
  const [released, setReleased] = useState(false);
  const start = useRef<{ y: number; t: number; id: number } | null>(null);
  const last = useRef<{ y: number; t: number }>({ y: 0, t: 0 });
  const from = useRef(0);

  const onDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (phase || !onClose) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { y: e.clientY, t: e.timeStamp, id: e.pointerId };
    last.current = { y: e.clientY, t: e.timeStamp };
    setSettle(false);
    setDrag(0);
  }, [phase, onClose]);
  const onMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!start.current || e.pointerId !== start.current.id) return;
    last.current = { y: e.clientY, t: e.timeStamp };
    setDrag(Math.max(0, e.clientY - start.current.y));
  }, []);
  const onUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!start.current || e.pointerId !== start.current.id) return;
    const dy = Math.max(0, e.clientY - start.current.y);
    const dt = Math.max(1, e.timeStamp - last.current.t);
    const v = (e.clientY - last.current.y) / dt;
    const h = box.current?.getBoundingClientRect().height ?? 1;
    start.current = null;
    const dismiss = dy > h * PULL_SHARE || (v > FLICK_PX_PER_MS && dy > 24);
    if (dismiss) {
      // Hold the sheet where the finger left it and ask. If the content
      // closes, the slide down starts from here (see the effect below); if
      // it does not, the sheet springs back. `released` is its own flag: the
      // offset is already what the last move set it to, so setting it again
      // would change nothing and the effect would never run (measured: the
      // sheet sat 320px down behind the leave dialog).
      from.current = dy;
      setDrag(dy);
      setReleased(true);
      onClose?.();
    } else {
      setSettle(true);
      setDrag(null);
    }
  }, [box, onClose]);

  // A release past the threshold that did NOT close — the content asked a
  // question instead — springs back. NOT ON THE NEXT FRAME (Freddi, 2026-10-13:
  // "dragging the drawers down … causing the drawers to bounce back up and then
  // slide down, it should slide down with my finger"): a drawer's close is a
  // route change, and on a phone it lands frames after the release — the sheet
  // had already sprung back to the top and reset its start, so the slide down
  // began from there (measured at 6× CPU: let go at 332px, back to 12px, then
  // down). It holds where the finger left it until the close has had time to
  // land; still open after that, a question is up, and it springs back behind it.
  useEffect(() => {
    if (!released) return;
    if (!open) { setReleased(false); return; }
    const id = window.setTimeout(() => {
      // Sprung back: the next close starts from the top, not from where this pull left off (#245 review).
      from.current = 0;
      setReleased(false); setSettle(true); setDrag(null);
    }, CLOSE_GRACE_MS);
    return () => window.clearTimeout(id);
  }, [released, open]);

  const dragging = drag !== null && start.current !== null;
  const h = box.current?.getBoundingClientRect().height ?? 1;
  const progress = drag === null ? 0 : Math.min(1, drag / h);
  const cls = `dw${compact ? ' compact' : ''}${phase ? ` ${phase}` : ''}${dragging ? ' dragging' : ''}${settle ? ' settle' : ''}`;
  const style: React.CSSProperties = {};
  if (drag !== null && !phase) style.transform = `translateY(${drag}px)`;
  if (phase === 'out' && from.current) style['--dw-from' as never] = `${from.current}px` as never;
  return (
    <>
      <div className={`dw-scrim${phase ? ` ${phase}` : ''}`} aria-hidden="true" style={progress ? { opacity: 1 - progress * 0.7 } : undefined} />
      <div className={cls} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={box} onAnimationEnd={onAnimationEnd} style={style} onTransitionEnd={(e) => { if (e.target === e.currentTarget) setSettle(false); }}>
        <div className="dw-grip" aria-hidden="true" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}><i /></div>
        {children}
      </div>
    </>
  );
}
